import { apiClient } from "./client";
import type {
  CampaignListResponse,
  CampaignRead,
  EnrollRequest,
  OutreachStatus,
  RemoveResponse,
  UnenrollResponse,
} from "./types";

export async function getOutreach(
  slug: string,
  companyId: number,
): Promise<OutreachStatus> {
  const res = await apiClient.get<OutreachStatus>(
    `/projects/${slug}/companies/${companyId}/outreach`,
  );
  return res.data;
}

export async function listCampaigns(slug: string): Promise<CampaignRead[]> {
  const res = await apiClient.get<CampaignListResponse>(
    `/projects/${slug}/outreach/campaigns`,
  );
  return res.data.campaigns;
}

/**
 * Enroll a contact in a Lemlist campaign. Re-enrolling on the same campaign
 * for a paused lead is the canonical "resume" path — Lemlist unpauses the
 * lead instead of creating a duplicate.
 */
export async function enrollCompany(
  slug: string,
  companyId: number,
  payload: EnrollRequest,
): Promise<OutreachStatus> {
  const res = await apiClient.post<OutreachStatus>(
    `/projects/${slug}/companies/${companyId}/outreach/enroll`,
    payload,
  );
  return res.data;
}

/**
 * Pause the lead in Lemlist (reversible). The lead stays in the campaign
 * locally and on Lemlist's side; POST /enroll on the same campaign resumes
 * via Lemlist's unpause path. Backend route: POST /outreach/unenroll.
 */
export async function pauseOutreach(
  slug: string,
  companyId: number,
): Promise<UnenrollResponse> {
  const res = await apiClient.post<UnenrollResponse>(
    `/projects/${slug}/companies/${companyId}/outreach/unenroll`,
  );
  return res.data;
}

/**
 * Hard-remove the lead from Lemlist (irreversible). Backend clears every
 * cached lemlist_* metadata key. Reserved for the rare destructive case;
 * the contact remains re-subscribable for future campaigns. Backend route:
 * POST /outreach/remove.
 */
export async function removeOutreach(
  slug: string,
  companyId: number,
): Promise<RemoveResponse> {
  const res = await apiClient.post<RemoveResponse>(
    `/projects/${slug}/companies/${companyId}/outreach/remove`,
  );
  return res.data;
}
